import { TravelPremiumRisk } from '@app/domain/model/eshop/travel/TravelPremiumRisk';
import { TravelRiskPackageCode } from '@app/domain/model/eshop/travel/TravelRiskPackageCode';
import { ChoiceButtonInput, ChoiceButtonOption } from '@app/app/_components/Input/ChoiceButtonInput';
import { Tag } from '@app/app/_components/Tag/Tag';
import { Heading } from '@app/app/_components/Heading/Heading';
import { Paragraph } from '@app/app/_components/Paragraph/Paragraph';
import { ChecklistItem } from '@app/app/_components/ChecklistItem/ChecklistItem';
import { splitNumber } from '@app/lib/split-number/splitNumber';
import { formatEuroValue } from '@app/infrastructure/application/formatEuroValue';
import { useTranslations } from 'next-intl';
import { ChangeEvent, useMemo } from 'react';
import { getRiskIcon } from '@app/app/esales/travel/_sections/TravelWizardSection/helpers/getRiskIcon';
import { determineRiskDetails } from '@app/app/esales/travel/_sections/TravelWizardSection/helpers/determineRiskDetails';
import { determineTravelRiskName } from '@app/app/esales/travel/_sections/TravelWizardSection/helpers/determineTravelRiskName';
import { Message } from '@app/app/_components/Message/Message';
import { ExpandableText } from '@app/app/_components/ExpandableText/ExpandableText';
import { useAppParameters } from '@app/app/_context/AppParametersContext/useAppParameters';

interface Props {
    risks: TravelPremiumRisk[];
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    value?: string[];
    defaultChecked?: string[];
}

export function InsuranceRisksStep({ risks, onChange, value, defaultChecked }: Props) {
    const { tenant } = useAppParameters();
    const t = useTranslations();

    const options: ChoiceButtonOption[] = useMemo(() => {
        return risks.map(risk => {
            const hasDiscount =
                risk.premiumAfterDiscount != null &&
                risk.premium != null &&
                risk.premiumAfterDiscount > 0 &&
                risk.premiumAfterDiscount < risk.premium;

            const displayPrice = hasDiscount ? risk.premiumAfterDiscount! : (risk.premium ?? 0);
            const splittedPrice = splitNumber(displayPrice);

            let discountPercentage = 0;
            if (hasDiscount && risk.premium) {
                discountPercentage = ((risk.premium - risk.premiumAfterDiscount!) / risk.premium) * 100;
            }

            return {
                value: risk.code ?? '',
                disabled: risk.code === TravelRiskPackageCode.MedicalExpenses,
                label: (
                    <div className="flex flex-col gap-3 md:flex-row md:items-start md:gap-4">
                        <div className="shrink-0">{getRiskIcon(risk.code)}</div>

                        <div className="flex flex-1 flex-col gap-3">
                            <div className="flex flex-wrap items-center gap-3">
                                <Heading level="h4" className="text-xl">
                                    {risk.name || determineTravelRiskName(risk.code)}
                                </Heading>
                                {hasDiscount && (
                                    <Tag variant="promo">
                                        {t('eshop.travel.save')} {discountPercentage.toFixed(0)}%
                                    </Tag>
                                )}
                            </div>

                            <div className="flex flex-col gap-1">
                                {hasDiscount && risk.premium && (
                                    <Paragraph isGray size="tiny" className="line-through">
                                        {formatEuroValue(risk.premium, tenant)}
                                    </Paragraph>
                                )}
                                <div
                                    className="flex items-baseline gap-1"
                                    aria-label={`${splittedPrice.integerPart},${splittedPrice.fractionalPart} € / ${t('eshop.travel.forAllInsured')}`}>
                                    <Paragraph ariaHidden="true" isBold size="medium">
                                        {splittedPrice.integerPart}
                                    </Paragraph>
                                    <Paragraph ariaHidden="true" isBold size="tiny" className="self-start">
                                        {splittedPrice.fractionalPart}
                                    </Paragraph>
                                    <Paragraph ariaHidden="true" isBold size="medium">
                                        €
                                    </Paragraph>
                                    <Paragraph ariaHidden="true" isGray className="ml-1">
                                        {`/ ${t('eshop.travel.forAllInsured')}`}
                                    </Paragraph>
                                </div>
                            </div>
                        </div>
                    </div>
                ),
                details: (
                    <div className="flex flex-col gap-4">
                        {determineRiskDetails(risk.code).map((translationKey, index) => (
                            <ChecklistItem key={index}>
                                <Paragraph>{t(translationKey)}</Paragraph>
                            </ChecklistItem>
                        ))}
                    </div>
                ),
            };
        });
    }, [risks, t, tenant]);

    return (
        <div className="flex flex-col gap-6">
            <Message variant="info">
                {
                    <div className="flex flex-col gap-2">
                        <Paragraph isBold>{t('eshop.travel.importantInformationTitle').toUpperCase()}</Paragraph>
                        <Paragraph size="small">
                            <ExpandableText maxLines={3}>
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: t.raw('eshop.travel.importantInformationDescription'),
                                    }}
                                />
                            </ExpandableText>
                        </Paragraph>
                    </div>
                }
            </Message>
            <ChoiceButtonInput
                name="travelRisks"
                type="checkbox"
                options={options}
                onChange={onChange}
                value={value}
                defaultChecked={defaultChecked}
            />
        </div>
    );
}
